/**
 * Which office a patient search hit belongs to, for All offices mode: the
 * office's short code from the office selector (`officeInitials`, the same
 * monogram as the selector's chips: "MB", "A2", "MC") in a small translucent
 * orange tag next to the name. With one office selected every hit is that
 * office's, so nothing is shown.
 */
import type { ShellOffice } from '@velora/contracts/shell';
import { officeInitials } from '@velora/ui';
import { useOfficeScope } from './OfficeScope.js';
import './patient-office-tag.css';

/** The office fields a patient search hit may carry (both search APIs). */
export interface OfficeTaggedHit {
  /** The office the hit is listed under (newer servers). */
  locationId?: string | null | undefined;
  /** Every office in scope the patient belongs to, home office first (newer servers). */
  officeIds?: readonly string[] | undefined;
  homeLocationId?: string | null | undefined;
}

export interface OfficeTagItem {
  id: string;
  code: string;
  name: string;
}

/**
 * The offices to tag a hit with, in order (home office first), limited to
 * `max` with the rest counted. Offices this user cannot see are skipped.
 */
export function patientOfficeTags(
  hit: OfficeTaggedHit,
  offices: readonly Pick<ShellOffice, 'id' | 'name' | 'shortName'>[],
  max = 2,
): { shown: OfficeTagItem[]; more: number } {
  const ids =
    hit.officeIds && hit.officeIds.length > 0
      ? hit.officeIds
      : [hit.locationId ?? hit.homeLocationId].filter((x): x is string => !!x);
  const known: OfficeTagItem[] = [];
  for (const id of new Set(ids)) {
    const o = offices.find((x) => x.id === id);
    if (o) known.push({ id: o.id, code: officeInitials(o), name: o.name });
  }
  return { shown: known.slice(0, max), more: Math.max(0, known.length - max) };
}

/** The office tag for one hit; `null` outside All offices or with one office. */
export function PatientOfficeTag({
  hit,
  max = 2,
  className,
  always = false,
}: {
  hit: OfficeTaggedHit;
  max?: number;
  className?: string;
  /** Tag even with one office selected (pickers that search every office). */
  always?: boolean;
}) {
  const { isAll, offices } = useOfficeScope();
  if ((!isAll && !always) || offices.length < 2) return null;
  const { shown, more } = patientOfficeTags(hit, offices, max);
  if (shown.length === 0) return null;
  const names = shown.map((o) => o.name).join(', ');
  return (
    <span
      className={className ? `vel-pt-offices ${className}` : 'vel-pt-offices'}
      title={more > 0 ? `${names} and ${more} more` : names}
      data-testid="patient-office-tag"
    >
      {shown.map((o) => (
        <span key={o.id} className="vel-pt-office" data-office={o.id} aria-hidden>
          {o.code}
        </span>
      ))}
      {more > 0 ? (
        <span className="vel-pt-office vel-pt-office--more" aria-hidden>
          +{more}
        </span>
      ) : null}
      <span className="vel-visually-hidden">
        {` (${shown.length + more > 1 ? 'offices' : 'office'}: ${names}${more > 0 ? ` and ${more} more` : ''})`}
      </span>
    </span>
  );
}
